import { ArrowLeft, Brain, Layout, Download, User, History, Code, Layers, Database, Cpu, Settings, Zap } from 'lucide-react'
import { Link } from 'react-router-dom'

const features = [
  {
    icon: <Brain size={24} />,
    title: 'AI-Powered Tailoring',
    description: 'Uses NVIDIA NIM API (minimaxai/minimax-m2.1) to analyze job descriptions and automatically customize resumes and cover letters for maximum relevance.',
  },
  {
    icon: <Layout size={24} />,
    title: 'Multiple Templates',
    description: 'Three professionally designed templates — Modern, Classic, and Minimalist — each optimized for different industries and ATS compatibility.',
  },
  {
    icon: <Download size={24} />,
    title: 'PDF Export',
    description: 'One-click PDF generation using html2pdf.js with precise layout control, proper page breaks, and print-ready output quality.',
  },
  {
    icon: <User size={24} />,
    title: 'Profile Management',
    description: 'Persistent profile storage via Chrome Storage API. Manage multiple professional personas and switch contexts seamlessly.',
  },
  {
    icon: <History size={24} />,
    title: 'History Tracking',
    description: 'Full audit trail of all generated documents with timestamps, job references, and quick re-generation capabilities.',
  },
  {
    icon: <Zap size={24} />,
    title: 'ATS Scoring',
    description: 'Built-in Applicant Tracking System score estimation that highlights keyword gaps and suggests optimization improvements.',
  },
]

const architectureLayers = [
  {
    name: 'UI Layer',
    tech: 'React + Vite',
    components: 'Popup, Options Page, Template Preview, Profile Editor',
    description: 'Chrome extension popup for quick actions, full options page for profile management, and live template preview with real-time rendering.',
  },
  {
    name: 'Domain Layer',
    tech: 'TypeScript Services',
    components: 'ResumeBuilder, CoverLetterGenerator, ATSScorer, TemplateEngine',
    description: 'Core business logic handling document assembly, template injection, ATS keyword analysis, and PDF generation orchestration.',
  },
  {
    name: 'Data Layer',
    tech: 'Chrome Storage API',
    components: 'ProfileStore, HistoryStore, SettingsStore',
    description: 'Persistent local storage for user profiles, generation history, and extension settings with sync capabilities across devices.',
  },
  {
    name: 'AI Service',
    tech: 'NVIDIA NIM API',
    components: 'LLM Client, Prompt Builder, Response Parser',
    description: 'External AI integration using minimaxai/minimax-m2.1 model for intelligent content generation, keyword extraction, and contextual rewriting.',
  },
]

export default function EasyResumePage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex justify-between items-center px-8 py-4 max-w-7xl mx-auto">
          <Link to="/" className="text-xl font-bold tracking-tighter text-white font-headline">
            Neural Slate
          </Link>
          <Link
            to="/#projects"
            className="text-on-surface-variant hover:text-primary transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back to Projects
          </Link>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center px-8 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent blur-3xl pointer-events-none"></div>
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <div className="max-w-4xl">
              <span className="inline-block py-1 px-3 mb-6 bg-surface-container-high border border-outline-variant/20 rounded-full text-primary text-xs font-mono tracking-widest uppercase">
                Firefox Extension · v1.3.0
              </span>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-8 leading-tight font-headline text-white">
                EasyResume <br />
                <span className="text-primary">AI Browser Extension</span>
              </h1>
              <p className="text-xl md:text-2xl text-on-surface-variant font-light mb-12 max-w-2xl leading-relaxed">
                AI-powered Firefox extension that tailors resumes and cover letters to job descriptions in real-time using NVIDIA NIM API.
              </p>
              <div className="flex flex-wrap gap-8 items-center">
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">3</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Resume Templates</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-primary text-4xl font-bold tracking-tighter font-headline">NIM</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">NVIDIA AI Model</span>
                </div>
                <div className="h-12 w-px bg-outline-variant/30"></div>
                <div className="flex flex-col">
                  <span className="text-secondary text-4xl font-bold tracking-tighter font-headline">ATS</span>
                  <span className="text-on-surface-variant text-sm uppercase tracking-widest font-medium">Score Estimation</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold mb-6 text-white font-headline">Project Overview</h2>
                <p className="text-on-surface-variant mb-8 leading-relaxed">
                  EasyResume is a Firefox browser extension that transforms the job application process by automatically tailoring your resume and cover letter to each specific job description using AI.
                </p>
                <div className="p-6 bg-surface-container-low rounded-xl border border-outline-variant/10">
                  <Brain className="text-primary mb-3" size={28} />
                  <h4 className="font-bold mb-2 text-white">AI-First Approach</h4>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    Leverages the minimaxai/minimax-m2.1 model via NVIDIA NIM API for intelligent content generation with contextual understanding of industry-specific terminology.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <div key={index} className="bg-surface-container-low p-6 rounded-xl border border-outline-variant/10 hover:border-primary/30 transition-all">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                        {feature.icon}
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 font-headline">{feature.title}</h3>
                      <p className="text-sm text-on-surface-variant leading-relaxed">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Architecture Section */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-white font-headline">Architecture Layers</h2>
              <p className="text-on-surface-variant max-w-2xl mx-auto">Four-layer architecture separating concerns across UI presentation, domain logic, data persistence, and AI integration.</p>
            </div>
            <div className="space-y-6">
              {architectureLayers.map((layer, index) => (
                <div key={index} className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10 flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    {index === 0 && <Layout size={24} />}
                    {index === 1 && <Cpu size={24} />}
                    {index === 2 && <Database size={24} />}
                    {index === 3 && <Brain size={24} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-white font-headline">{layer.name}</h3>
                      <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-1 rounded">{layer.tech}</span>
                    </div>
                    <p className="text-sm font-mono text-on-surface-variant/70 mb-3">{layer.components}</p>
                    <p className="text-on-surface-variant leading-relaxed">{layer.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tech Stack */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold mb-6 text-white font-headline">Tech Stack</h2>
                <p className="text-on-surface-variant mb-8 leading-relaxed">
                  Modern web extension architecture built for performance, maintainability, and seamless browser integration.
                </p>
                <div className="p-6 bg-surface-container-low rounded-xl border border-outline-variant/10">
                  <Code className="text-primary mb-3" size={28} />
                  <h4 className="font-bold mb-2 text-white">Developer Experience</h4>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    Hot module replacement via Vite, TypeScript strict mode, and vite-plugin-web-extension for streamlined development workflow.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="overflow-hidden rounded-xl border border-outline-variant/15">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-surface-container-high text-on-surface uppercase text-xs tracking-widest">
                      <tr>
                        <th className="px-6 py-4">Category</th>
                        <th className="px-6 py-4">Technology</th>
                        <th className="px-6 py-4">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10">
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Framework</td>
                        <td className="px-6 py-6 text-on-surface-variant">React + TypeScript</td>
                        <td className="px-6 py-6 text-on-surface-variant">UI component architecture with type safety</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Build Tool</td>
                        <td className="px-6 py-6 text-on-surface-variant">Vite</td>
                        <td className="px-6 py-6 text-on-surface-variant">Fast HMR and optimized production builds</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Extension Plugin</td>
                        <td className="px-6 py-6 text-on-surface-variant">vite-plugin-web-extension</td>
                        <td className="px-6 py-6 text-on-surface-variant">Manifest v3 bundling for Firefox</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">AI API</td>
                        <td className="px-6 py-6 text-on-surface-variant">NVIDIA NIM (minimax-m2.1)</td>
                        <td className="px-6 py-6 text-on-surface-variant">Resume and cover letter generation</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">PDF Engine</td>
                        <td className="px-6 py-6 text-on-surface-variant">html2pdf.js</td>
                        <td className="px-6 py-6 text-on-surface-variant">Client-side PDF generation</td>
                      </tr>
                      <tr className="hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-6 font-bold text-primary">Storage</td>
                        <td className="px-6 py-6 text-on-surface-variant">Chrome Storage API</td>
                        <td className="px-6 py-6 text-on-surface-variant">Persistent profile and history management</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Setup & Configuration */}
        <section className="py-24 px-8 bg-surface">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-white font-headline text-center">Setup & Configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-6">
                  <Settings className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Installation</h3>
                </div>
                <div className="space-y-4 text-on-surface-variant">
                  <p className="text-sm leading-relaxed">1. Clone the repository and install dependencies with npm.</p>
                  <p className="text-sm leading-relaxed">2. Configure NVIDIA NIM API key in the extension options page.</p>
                  <p className="text-sm leading-relaxed">3. Load as temporary add-on in Firefox via about:debugging.</p>
                  <p className="text-sm leading-relaxed">4. Set up your professional profile with experiences and skills.</p>
                </div>
              </div>
              <div className="bg-surface-container-low p-8 rounded-xl border border-outline-variant/10">
                <div className="flex items-center gap-3 mb-6">
                  <Layers className="text-primary" size={24} />
                  <h3 className="text-xl font-bold text-white font-headline">Usage Flow</h3>
                </div>
                <div className="space-y-4 text-on-surface-variant">
                  <p className="text-sm leading-relaxed">1. Navigate to a job posting on any supported platform.</p>
                  <p className="text-sm leading-relaxed">2. Click the extension icon to analyze the job description.</p>
                  <p className="text-sm leading-relaxed">3. Select a template and review the AI-generated content.</p>
                  <p className="text-sm leading-relaxed">4. Export as PDF or save to history for later reference.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-8 bg-surface-container-lowest">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-white font-headline">Interested in the Code?</h2>
            <p className="text-on-surface-variant mb-10 leading-relaxed">
              The full extension source code, including AI prompt engineering, template system, and Chrome Storage integration is available for review.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://github.com/Viresh26"
                target="_blank"
                rel="noopener noreferrer"
                className="px-10 py-4 bg-primary text-on-primary font-headline font-bold rounded-xl transition-all hover:shadow-[0_0_30px_rgba(164,230,255,0.3)]"
              >
                GITHUB REPOSITORY
              </a>
              <Link
                to="/#projects"
                className="px-10 py-4 bg-surface-container-high text-on-surface font-headline font-bold rounded-xl border border-outline-variant/30 hover:bg-surface-container-highest transition-all"
              >
                VIEW ALL PROJECTS
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
    </div>
  )
}
